import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Typography } from "@/components/Typography";
import { trackGaEvent } from "@/features/ga/trackGaEvent";
import { useEnableMcpConnections } from "@/features/mcp/useEnableMcpConnections";
import { useEnableOpenApiV2KeySelfServe } from "@/features/openapi/useEnableOpenApiV2KeySelfServe";
import { interlude } from "@/interlude";
import { ApplicationCard } from "@/pages/applications/components/ApplicationCard";
import { shouldShowMcpContactUs } from "@/pages/applications/mcpContactUs";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";
import CresclabImage from "@/statics/cresclab.png";

const styles = {
  cardContainer: {
    display: "grid",
    gap: "20px",
    gridAutoRows: "auto",
    gridTemplateColumns: "repeat(auto-fill, minmax(min(330px, 100%), 1fr))",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

/**
 * Credentials other systems use to reach the organization: OpenAPI keys and
 * authorized MCP clients. The pages behind the cards keep their
 * `/applications/...` paths because MAAC links to `applications/api-keys`.
 */
export const DeveloperToolsPage: React.FC = function DeveloperToolsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const organizationSettingQuery =
    interlude.organization.useOrganizationSetting();

  const enableOpenApi = useEnableOpenApiV2KeySelfServe();

  const enableMcpConnections = useEnableMcpConnections();

  // Fail closed: an org whose setting has not loaded is not entitled yet.
  const openApiV2Enabled =
    organizationSettingQuery.data?.openApiV2Enabled ?? false;

  /**
   * Only asked for once the organization is entitled. The endpoint is gated on
   * the same setting and answers 403 `OPENAPI_V2_NOT_ENABLED` otherwise, so an
   * ungated call would sit rejected behind every "Contact us" card. The flag
   * is in the condition too: nothing should be fetched for a card this build
   * does not render.
   */
  const apiKeyListQuery = interlude.apiKey.useListApiKeys(undefined, {
    enabled: enableOpenApi && openApiV2Enabled,
  });

  // Fail closed, as `openApiV2Enabled` above.
  const mcpEnabled = organizationSettingQuery.data?.mcpEnabled ?? false;

  /**
   * Not gated on `mcpEnabled`, unlike the API key list: interlude answers this
   * endpoint for every organization, and the count is what decides whether an
   * organization without the entitlement still gets into the page.
   */
  const mcpApiKeyListQuery = interlude.mcp.useListMcpApiKeys(undefined, {
    enabled: enableMcpConnections,
  });

  const hasMcpConnections = (mcpApiKeyListQuery.data?.data.length ?? 0) > 0;

  const showMcpContactUs = shouldShowMcpContactUs({
    mcpEnabled,
    hasMcpConnections,
    isMcpConnectionListError: mcpApiKeyListQuery.isError,
  });

  const activeApiKeyCount = useMemo(() => {
    if (!apiKeyListQuery.isSuccess) return undefined;
    return apiKeyListQuery.data.data.filter(
      (apiKey) => apiKey.status.expected && apiKey.status.value === "active",
    ).length;
  }, [apiKeyListQuery.data, apiKeyListQuery.isSuccess]);

  const hasApiKeys = (activeApiKeyCount ?? 0) > 0;

  /**
   * A disabled query stays `pending` forever, so the card cannot read
   * `isPending` on its own -- an organization without the entitlement would
   * spin instead of offering "Contact us". Only the entitled case waits for
   * the key count.
   */
  const isOpenApiLoading =
    organizationSettingQuery.isPending ||
    (enableOpenApi && openApiV2Enabled && apiKeyListQuery.isPending);

  const isMcpLoading =
    organizationSettingQuery.isPending || mcpApiKeyListQuery.isPending;

  const onOpenApiClick = () => {
    if (openApiV2Enabled) {
      navigate({ to: "/applications/api-keys" });
    } else {
      trackGaEvent("openApiContactUsClick");
      window.open(t("newRequest.link"), "_blank", "noopener,noreferrer");
    }
  };

  const onMcpClick = () => {
    if (showMcpContactUs) {
      trackGaEvent("mcpContactUsClick");
      window.open(t("newRequest.link"), "_blank", "noopener,noreferrer");
    } else {
      navigate({ to: "/applications/mcp-connections" });
    }
  };

  return (
    <PageLayout>
      <Stack gap="4px">
        <PageTitle>
          {t("applications.developerTools.title", "Developer tools")}
        </PageTitle>
        <Typography variant="body">
          {t(
            "applications.developerTools.description",
            "Manage credentials used by your systems and integrations.",
          )}
        </Typography>
      </Stack>
      <Box sx={styles.cardContainer}>
        {enableOpenApi ? (
          <ApplicationCard
            title={t("applications.openApi.title", "OpenAPI")}
            description={t(
              "applications.openApi.description",
              "Manage the API keys your systems use to call Crescendo Lab OpenAPI v2.",
            )}
            detailText={
              activeApiKeyCount === undefined
                ? undefined
                : t("applications.openApi.activeKeyCount", {
                    count: activeApiKeyCount,
                    defaultValue: "Active API keys: {{count}}",
                  })
            }
            learnMoreLink={t(
              "applications.openApi.learnMoreUrl",
              "https://doc.user360.cresclab.com/v2/",
            )}
            learnMoreText={t("common.learnMore")}
            buttonText={
              openApiV2Enabled
                ? t("applications.openApi.manage", "Manage")
                : t("common.contactUs")
            }
            onButtonClick={onOpenApiClick}
            imageUrl={CresclabImage}
            /**
             * A key count that failed to load leaves the badge off, which
             * looks the same as having no keys. Deliberately not given a
             * third state: the card's only job is to offer the way in, and
             * the page behind it reports a failed load on its own rather
             * than claiming the organization has none.
             */
            isConnected={openApiV2Enabled && hasApiKeys}
            isLoading={isOpenApiLoading}
            // "Configured", not "Connected": keys are created here, not linked
            // from another system.
            connectedLabel={t("common.configured", "Configured")}
            /**
             * "Manage", not the component's default "Edit". Keys are revoked
             * and disabled more than they are edited, the page it opens is
             * called API Key Management, and this card already says "Manage"
             * before any key exists -- so the label does not change under the
             * reader when their first key is issued.
             */
            connectedButtonText={t("applications.openApi.manage", "Manage")}
          />
        ) : null}
        {enableMcpConnections ? (
          <ApplicationCard
            title={t("applications.mcp.title", "Crescendo Lab MCP")}
            description={t(
              "applications.mcp.description",
              "See and revoke the AI clients authorized to access your organization through Crescendo Lab MCP.",
            )}
            buttonText={
              showMcpContactUs
                ? t("common.contactUs")
                : t("applications.mcp.manage", "Manage")
            }
            onButtonClick={onMcpClick}
            imageUrl={CresclabImage}
            isConnected={hasMcpConnections}
            isLoading={isMcpLoading}
            connectedLabel={t("common.connected")}
            // "Manage" for the same reason as the OpenAPI card above.
            connectedButtonText={t("applications.mcp.manage", "Manage")}
          />
        ) : null}
      </Box>
    </PageLayout>
  );
};
